import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cities = [
  'Genève',
  'Lausanne',
  'Montreux',
  'Verbier',
  'Crans-Montana',
  'Villars',
  'Fribourg',
  'Neuchâtel',
  'Valais'
];

export const POC = () => {
  const [activeTab, setActiveTab] = useState('louer');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    bienType: '',
    objectif: '',
    budget: '',
    details: ''
  });
  
  const [errors, setErrors] = useState({});
  
  // Real-time validation
  const validateField = (field, value) => {
    const newErrors = { ...errors };
    
    switch(field) {
      case 'name':
        if (!value || value.trim().length < 2) {
          newErrors.name = 'Nom requis (min 2 caractères)';
        } else {
          delete newErrors.name;
        }
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value || !emailRegex.test(value)) {
          newErrors.email = 'Email valide requis';
        } else {
          delete newErrors.email;
        }
        break;
      case 'phone':
        if (!value || value.trim().length < 8) {
          newErrors.phone = 'Téléphone requis (min 8 chiffres)';
        } else {
          delete newErrors.phone;
        }
        break;
      case 'city':
        if (!value) {
          newErrors.city = 'Ville requise';
        } else {
          delete newErrors.city;
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
  };
  
  // Check if form is valid
  const isFormValid = () => {
    return (
      formData.name.trim().length >= 2 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
      formData.phone.trim().length >= 8 &&
      formData.city &&
      Object.keys(errors).length === 0
    );
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Validate on change
    if (['name', 'email', 'phone', 'city'].includes(field)) {
      validateField(field, value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Capture tracking data
      const urlParams = new URLSearchParams(window.location.search);
      const trackingData = {
        source: urlParams.get('utm_source') || 'direct',
        campaign: urlParams.get('utm_campaign') || null,
        page_origin: window.location.href,
        referrer: document.referrer || null
      };

      const leadData = {
        service: activeTab,
        pole: getPoleFromService(activeTab),
        city: formData.city,
        answers: {
          bienType: formData.bienType,
          objectif: formData.objectif,
          budget: formData.budget,
          details: formData.details
        },
        contact: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        },
        ...trackingData
      };

      await axios.post(`${API}/leads`, leadData);
      
      toast.success('Demande envoyée avec succès !', {
        description: 'Nous vous contacterons sous 24h.',
        duration: 5000
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        city: '',
        bienType: '',
        objectif: '',
        budget: '',
        details: ''
      });
      setErrors({});
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error('Erreur lors de l\'envoi', {
        description: 'Veuillez réessayer ou nous contacter directement.',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const getPoleFromService = (service) => {
    const mapping = {
      louer: 'conciergerie',
      vendre: 'immo',
      renover: 'renovation',
      clients: 'marketing'
    };
    return mapping[service] || null;
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8" data-testid="poc-page">
      <div className="mx-auto w-full max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Homely Solutions — POC
          </h1>
          <p className="text-muted-foreground text-lg">
            Testez le système de capture de leads intelligent
          </p>
        </div>

        <Card className="shadow-lg" data-testid="poc-lead-form-card">
          <CardHeader>
            <CardTitle>Quel est votre besoin ?</CardTitle>
            <CardDescription>
              Sélectionnez votre situation et remplissez le formulaire adapté
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="poc-need-tabs">
              <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-8">
                <TabsTrigger value="louer" data-testid="tab-louer">Louer (Airbnb)</TabsTrigger>
                <TabsTrigger value="vendre" data-testid="tab-vendre">Vendre</TabsTrigger>
                <TabsTrigger value="renover" data-testid="tab-renover">Rénover</TabsTrigger>
                <TabsTrigger value="clients" data-testid="tab-clients">Avoir des clients</TabsTrigger>
              </TabsList>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Common fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nom complet *</Label>
                    <Input
                      id="name"
                      data-testid="input-name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Jean Dupont"
                      required
                      className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="input-email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="jean@example.com"
                      required
                      className={errors.email ? 'border-red-500' : ''}
                    />
                    {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Téléphone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      data-testid="input-phone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="+41 79 123 45 67"
                      required
                      className={errors.phone ? 'border-red-500' : ''}
                    />
                    {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <Label htmlFor="city">Ville / Zone *</Label>
                    <Select 
                      value={formData.city} 
                      onValueChange={(val) => handleInputChange('city', val)} 
                      required
                    >
                      <SelectTrigger 
                        id="city" 
                        data-testid="select-city"
                        className={`h-11 ${errors.city ? 'border-red-500' : ''}`}
                      >
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {cities.map(city => (
                          <SelectItem key={city} value={city} className="py-3 cursor-pointer hover:bg-accent">
                            {city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
                  </div>
                </div>

                {/* Dynamic fields based on tab */}
                <TabsContent value="louer" className="space-y-4 mt-0">
                  <div>
                    <Label htmlFor="bienType">Type de bien</Label>
                    <Select value={formData.bienType} onValueChange={(val) => handleInputChange('bienType', val)}>
                      <SelectTrigger id="bienType" data-testid="select-bien-type" className="h-11">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="appartement" className="py-3 cursor-pointer hover:bg-accent">Appartement</SelectItem>
                        <SelectItem value="chalet" className="py-3 cursor-pointer hover:bg-accent">Chalet</SelectItem>
                        <SelectItem value="villa" className="py-3 cursor-pointer hover:bg-accent">Villa</SelectItem>
                        <SelectItem value="studio" className="py-3 cursor-pointer hover:bg-accent">Studio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="objectif">Objectif de revenu mensuel</Label>
                    <Input
                      id="objectif"
                      data-testid="input-objectif"
                      value={formData.objectif}
                      onChange={(e) => handleInputChange('objectif', e.target.value)}
                      placeholder="Ex: 3000 CHF/mois"
                      className="h-11"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="vendre" className="space-y-4 mt-0">
                  <div>
                    <Label htmlFor="bienType">Type de bien</Label>
                    <Input
                      id="bienType"
                      data-testid="input-bien-type-vendre"
                      value={formData.bienType}
                      onChange={(e) => handleInputChange('bienType', e.target.value)}
                      placeholder="Ex: Appartement 3.5 pièces"
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label htmlFor="objectif">Estimation souhaitée</Label>
                    <Input
                      id="objectif"
                      data-testid="input-estimation"
                      value={formData.objectif}
                      onChange={(e) => handleInputChange('objectif', e.target.value)}
                      placeholder="Ex: 850'000 CHF"
                      className="h-11"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="renover" className="space-y-4 mt-0">
                  <div>
                    <Label htmlFor="bienType">Type de rénovation</Label>
                    <Select value={formData.bienType} onValueChange={(val) => handleInputChange('bienType', val)}>
                      <SelectTrigger id="bienType" data-testid="select-renovation-type" className="h-11">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="salle-de-bain" className="py-3 cursor-pointer hover:bg-accent">Salle de bain</SelectItem>
                        <SelectItem value="cuisine" className="py-3 cursor-pointer hover:bg-accent">Cuisine</SelectItem>
                        <SelectItem value="peinture" className="py-3 cursor-pointer hover:bg-accent">Peinture</SelectItem>
                        <SelectItem value="parquet" className="py-3 cursor-pointer hover:bg-accent">Parquet</SelectItem>
                        <SelectItem value="carrelage" className="py-3 cursor-pointer hover:bg-accent">Carrelage</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="budget">Budget estimé</Label>
                    <Input
                      id="budget"
                      data-testid="input-budget"
                      value={formData.budget}
                      onChange={(e) => handleInputChange('budget', e.target.value)}
                      placeholder="Ex: 25'000 CHF"
                      className="h-11"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="clients" className="space-y-4 mt-0">
                  <div>
                    <Label htmlFor="bienType">Type d'activité</Label>
                    <Input
                      id="bienType"
                      data-testid="input-activite"
                      value={formData.bienType}
                      onChange={(e) => handleInputChange('bienType', e.target.value)}
                      placeholder="Ex: Agence immobilière, Restaurant..."
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label htmlFor="objectif">Objectif</Label>
                    <Select value={formData.objectif} onValueChange={(val) => handleInputChange('objectif', val)}>
                      <SelectTrigger id="objectif" data-testid="select-marketing-goal" className="h-11">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="leads" className="py-3 cursor-pointer hover:bg-accent">Générer des leads</SelectItem>
                        <SelectItem value="visibility" className="py-3 cursor-pointer hover:bg-accent">Augmenter visibilité</SelectItem>
                        <SelectItem value="automation" className="py-3 cursor-pointer hover:bg-accent">Automatiser le business</SelectItem>
                        <SelectItem value="website" className="py-3 cursor-pointer hover:bg-accent">Créer un site web</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>

                <div>
                  <Label htmlFor="details">Détails supplémentaires (optionnel)</Label>
                  <Textarea
                    id="details"
                    data-testid="textarea-details"
                    value={formData.details}
                    onChange={(e) => handleInputChange('details', e.target.value)}
                    placeholder="Toute information utile..."
                    rows={4}
                    className="resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base"
                  style={{ backgroundColor: 'var(--cta-blue)' }}
                  disabled={loading || !isFormValid()}
                  data-testid="submit-lead-button"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {loading ? 'Envoi en cours...' : 'Obtenir une estimation / un devis'}
                </Button>

                <p className="text-xs text-muted-foreground text-center mt-4">
                  Vos données sont sécurisées. Réponse sous 24h.
                </p>
              </form>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
